import api from "../api/axios";

// Audit log action categories
export type AuditLogCategory =
  | "user_management"
  | "patient_treatment_plan"
  | "appointments"
  | "settings";

// Audit log action types
export type AuditActionType =
  // User Management
  | "user_invited"
  | "user_activated"
  | "user_profile_edited"
  | "user_suspended"
  | "user_reactivated"
  | "user_2fa_enabled"
  | "user_2fa_disabled"
  // Patient and Treatment Plan
  | "patient_profile_viewed"
  | "treatment_plan_generated"
  | "treatment_plan_edited"
  | "treatment_plan_approved_provider"
  | "treatment_plan_sent_patient"
  | "treatment_plan_approved_patient"
  | "treatment_plan_rejected_patient"
  | "treatment_plan_status_updated"
  // Appointments
  | "appointment_scheduled"
  | "appointment_rescheduled"
  | "appointment_cancelled"
  | "appointment_file_attached"
  // Settings
  | "operating_hours_updated"
  | "procedure_duration_updated"
  | "notification_preferences_updated";

// Audit log status
export type AuditLogStatus = "success" | "failure";

// Audit log entry
export interface AuditLogEntry {
  id: number;
  userName: string;
  userId?: number;
  timestamp: string; // ISO datetime
  userRole: string;
  actionType: AuditActionType;
  actionCategory: AuditLogCategory;
  actionDescription: string;
  targetEntity?: string;
  status: AuditLogStatus;
  previousValues?: Record<string, unknown>;
  newValues?: Record<string, unknown>;
  description: string; // Human-readable full description
}

// Get audit logs params
export interface GetAuditLogsParams {
  search?: string;
  category?: AuditLogCategory;
  role?: string;
  dateFrom?: string;
  dateTo?: string;
  timeFrom?: string;
  timeTo?: string;
  start?: number;
  limit?: number;
  orderBy?: string;
  order?: "asc" | "desc";
}

// Get audit logs response
export interface GetAuditLogsResponse {
  data: AuditLogEntry[];
  total: number;
}

export interface AuditEventApiResponse {
  id: number;
  createdAt: string;
  userEmail: string;
  area: number;
  action: string;
  entityTitle: string;
  entityId: string;
  description: string;
  changes: Array<{
    fieldName: string;
    oldValue: string;
    newValue: string;
    changeType: number;
  }>;
  userName: string;
  userRole: string;
  isSuccess: boolean;
  failureReason: string;
  targetType: string;
  correlationId: string;
}

const mapAreaToCategory = (area: number): AuditLogCategory | undefined => {
  const areaMap: Record<number, AuditLogCategory> = {
    0: "user_management",
    1: "patient_treatment_plan",
    2: "appointments",
    3: "settings",
  };
  return areaMap[area];
};

const mapActionToActionType = (action: string): AuditActionType => {
  const actionMap: Record<string, AuditActionType> = {
    user_invited: "user_invited",
    user_activated: "user_activated",
    user_profile_edited: "user_profile_edited",
    user_suspended: "user_suspended",
    user_reactivated: "user_reactivated",
    user_2fa_enabled: "user_2fa_enabled",
    user_2fa_disabled: "user_2fa_disabled",
    patient_profile_viewed: "patient_profile_viewed",
    treatment_plan_generated: "treatment_plan_generated",
    treatment_plan_edited: "treatment_plan_edited",
    treatment_plan_approved_provider: "treatment_plan_approved_provider",
    treatment_plan_sent_patient: "treatment_plan_sent_patient",
    treatment_plan_approved_patient: "treatment_plan_approved_patient",
    treatment_plan_rejected_patient: "treatment_plan_rejected_patient",
    treatment_plan_status_updated: "treatment_plan_status_updated",
    appointment_scheduled: "appointment_scheduled",
    appointment_rescheduled: "appointment_rescheduled",
    appointment_cancelled: "appointment_cancelled",
    appointment_file_attached: "appointment_file_attached",
    operating_hours_updated: "operating_hours_updated",
    procedure_duration_updated: "procedure_duration_updated",
    notification_preferences_updated: "notification_preferences_updated",
  };
  return actionMap[action] || "user_activated";
};

const mapApiResponseToAuditLogEntry = (
  apiEvent: AuditEventApiResponse,
): AuditLogEntry => {
  const category = mapAreaToCategory(apiEvent.area);
  const previousValues: Record<string, unknown> = {};
  const newValues: Record<string, unknown> = {};

  if (apiEvent.changes && apiEvent.changes.length > 0) {
    apiEvent.changes.forEach((change) => {
      previousValues[change.fieldName] = change.oldValue;
      newValues[change.fieldName] = change.newValue;
    });
  }

  const actionType = mapActionToActionType(apiEvent.action);

  return {
    id: apiEvent.id,
    userName: apiEvent.userName || apiEvent.userEmail || "",
    timestamp: apiEvent.createdAt,
    userRole: apiEvent.userRole || "",
    actionType,
    actionCategory: category || "user_management",
    actionDescription: apiEvent.action || "",
    targetEntity: apiEvent.entityTitle || apiEvent.entityId,
    status: apiEvent.isSuccess ? "success" : "failure",
    previousValues:
      Object.keys(previousValues).length > 0 ? previousValues : undefined,
    newValues: Object.keys(newValues).length > 0 ? newValues : undefined,
    description: apiEvent.description || "",
  };
};

export const auditLogsService = {
  async getAuditLogs(
    params: GetAuditLogsParams,
  ): Promise<GetAuditLogsResponse> {
    const queryParams = new URLSearchParams();

    if (params.search) {
      queryParams.append("search", params.search);
    }

    if (params.dateFrom) {
      try {
        const date = new Date(params.dateFrom);
        if (!isNaN(date.getTime())) {
          queryParams.append("dateFrom", date.toISOString());
        }
      } catch (error) {
        console.error("Invalid dateFrom:", params.dateFrom, error);
      }
    }

    if (params.dateTo) {
      try {
        const date = new Date(params.dateTo + "T23:59:59");
        if (!isNaN(date.getTime())) {
          queryParams.append("dateTo", date.toISOString());
        }
      } catch (error) {
        console.error("Invalid dateTo:", params.dateTo, error);
      }
    }

    if (params.role) {
      queryParams.append("userRole", params.role);
    }

    if (params.category) {
      const categoryToTargetType: Record<AuditLogCategory, string> = {
        user_management: "User",
        patient_treatment_plan: "TreatmentPlan",
        appointments: "Appointment",
        settings: "Settings",
      };
      const targetType = categoryToTargetType[params.category];
      if (targetType) {
        queryParams.append("targetType", targetType);
      }
    }

    if (params.start !== undefined) {
      queryParams.append("start", params.start.toString());
    }

    if (params.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }

    queryParams.append("total", "true");

    if (params.orderBy) {
      const orderByMap: Record<string, string> = {
        timestamp: "CreatedAt",
        userName: "UserName",
        userRole: "UserRole",
        actionDescription: "Action",
      };
      const mappedField = orderByMap[params.orderBy];
      if (mappedField) {
        queryParams.append("order-by", mappedField);
      }
    }

    if (params.order) {
      queryParams.append("order", params.order === "asc" ? "0" : "1");
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/audit/events?${queryString}` : "/audit/events";

    try {
      const response = await api.get<AuditEventApiResponse[]>(url);

      const events = Array.isArray(response.data) ? response.data : [];
      const mappedLogs = events.map(mapApiResponseToAuditLogEntry);

      let total = 0;
      const xTotalCount = response.headers["x-total-count"];
      const totalHeader = response.headers["total"];

      if (xTotalCount) {
        const parsed = parseInt(String(xTotalCount), 10);
        if (!isNaN(parsed)) {
          total = parsed;
        }
      } else if (totalHeader) {
        const parsed = parseInt(String(totalHeader), 10);
        if (!isNaN(parsed)) {
          total = parsed;
        }
      }

      if (total === 0 && mappedLogs.length > 0) {
        total = mappedLogs.length;
      }

      return { data: mappedLogs, total };
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      return { data: [], total: 0 };
    }
  },
};
